
import React from 'react';
import { Insight } from '@/hooks/useFilteredInsights';
import { InsightCard as InsightCardType } from '@/types/insight';
import InsightCard from '@/components/insights/InsightCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface InsightsListProps {
  insights: Insight[];
  isLoading: boolean;
  error: Error | null;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const InsightsList = ({
  insights,
  isLoading,
  error,
  currentPage,
  totalPages,
  onPageChange
}: InsightsListProps) => {
  const navigate = useNavigate();

  const handleViewInsight = (insight: Insight) => {
    if (insight.slug) {
      navigate(`/insight/${insight.slug}`);
    }
  };

  // Transform insights to match InsightCard type
  const transformInsight = (insight: Insight): InsightCardType => ({
    id: insight.id,
    title: insight.title,
    category: insight.category?.name || '',
    agreePercentage: insight.agree_percentage || 0,
    totalPollsters: insight.total_pollsters || 0,
    description: insight.content || '',
    status: insight.published ? 'published' : 'draft',
    country_code: insight.country_code,
    constituency_code: null,
    linked_poll: insight.linked_poll,
    stats_status: insight.stats_status,
    slug: insight.slug || null
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {Array(8).fill(0).map((_, i) => (
          <div key={i} className="rounded-lg overflow-hidden shadow-md">
            <Skeleton className="h-40 w-full" />
            <div className="p-4 space-y-2">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
        <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">Error loading insights</h3>
        <p className="text-red-600 dark:text-red-300">{error.message}</p>
      </div>
    );
  }

  if (!insights.length) {
    return (
      <div className="text-center p-8 bg-muted/20 rounded-lg">
        <h3 className="text-lg font-semibold mb-2">No insights found</h3>
        <p className="text-muted-foreground">Try adjusting your filters or search terms</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {insights.map(insight => (
          <div key={insight.id}>
            <InsightCard 
              card={transformInsight(insight)} 
              onViewInsights={() => handleViewInsight(insight)} 
            />
          </div>
        ))}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination className="mt-8">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
                className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  isActive={page === currentPage}
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
                className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

export default InsightsList;
